/**
 * tableCrop.js — «Убрать стол»: отсечение деки томографа в объёмном виде.
 * [11.1.26, просьба владельца]
 *
 * ЗАЧЕМ. Дека стола рентгеноплотная — те же 100–700 HU, что и кость. Любой
 * костный или кожный пресет честно рисует её вместе с пациентом: на снимке
 * появляется «плита» под спиной, которая мешает смотреть и портит STL для
 * печати. Пресетом это не лечится: по плотности дека и кость неразличимы —
 * та же порода, что «Почки на нативе» (11.1.23). Лечится геометрией: стол
 * всегда СНИЗУ, значит нижнюю часть объёма можно просто не показывать.
 *
 * КАК. Плоскость отсечения (vtkPlane) на актёре объёма: данные не меняются,
 * меняется только показ, порог правится ползунком, выключатель возвращает
 * всё назад. Порог задаётся ДОЛЕЙ высоты объёма — не миллиметрами: у разных
 * исследований разный рост охвата, а «12% снизу» одинаково осмысленно.
 *
 * Здесь — расчёт (чистый, проверяется без vtk) и тонкий слой применения.
 */

/**
 * Точка и нормаль плоскости отсечения для доли высоты.
 *
 * [11.1.35] СТОРОНА ИСПРАВЛЕНА — прошлая редакция резала ПАЦИЕНТА, а не стол.
 * Здесь стояло «дека всегда со стороны МЕНЬШИХ значений оси» и нормаль +1:
 * плоскость сохраняла всё в сторону РОСТА Y. Но мировые координаты DICOM —
 * это LPS: ось Y растёт К СПИНЕ пациента (Posterior). Больной лежит на
 * спине, дека — ПОД спиной, то есть со стороны БОЛЬШИХ Y. Старая плоскость
 * оставляла спину со столом и срезала переднюю пластину туловища — владелец
 * увидел ровно это: «обрезает переднюю поверхность, а не стол». Проверка
 * verify_11_1_26 закрепляла ту же ошибку и переписана вместе с кодом.
 *
 * Теперь: уровень отсчитывается от ЗАДНЕЙ границы (макс Y), нормаль смотрит
 * в −Y — видимым остаётся всё СПЕРЕДИ от плоскости, срезается задняя
 * пластина толщиной «доля», где и лежит дека. Честная оговорка: если больной
 * снят НА ЖИВОТЕ (прон-укладка), стол окажется с передней стороны — тогда
 * этот переключатель срежет спину; по слову владельца добавим выбор стороны.
 *
 * @param {number[]} bounds [xMin,xMax,yMin,yMax,zMin,zMax] мировые границы объёма
 * @param {number} доля 0…0.9 — какую долю высоты срезать со стороны спины
 * @param {0|1|2} ось индекс оси «перед-зад» (по умолчанию Y = 1, LPS)
 * @returns {{origin:number[], normal:number[], уровень:number}|null}
 */
export function плоскостьОтсечения(bounds, доля = 0.12, ось = 1) {
  if (!Array.isArray(bounds) || bounds.length < 6) return null;
  const д = Math.min(0.9, Math.max(0, Number(доля) || 0));
  const мин = Number(bounds[ось * 2]);
  const макс = Number(bounds[ось * 2 + 1]);
  if (!Number.isFinite(мин) || !Number.isFinite(макс) || макс <= мин) return null;
  const уровень = макс - (макс - мин) * д;
  const origin = [
    (Number(bounds[0]) + Number(bounds[1])) / 2,
    (Number(bounds[2]) + Number(bounds[3])) / 2,
    (Number(bounds[4]) + Number(bounds[5])) / 2,
  ];
  origin[ось] = уровень;
  const normal = [0, 0, 0];
  normal[ось] = -1;   // видно то, что СПЕРЕДИ (меньшие Y); задняя пластина со столом скрыта
  return { origin, normal, уровень };
}

/** Есть ли у актёра рабочий маппер с плоскостями отсечения. */
export function маперПоддерживает(mapper) {
  return !!(mapper && typeof mapper.addClippingPlane === 'function'
    && typeof mapper.removeAllClippingPlanes === 'function');
}


/**
 * [11.1.46] СТОРОНА СРЕЗА. «Убрать стол» режет со стороны спины (+Y в LPS).
 * Владельцу понадобилось и обратное — смотреть органы сквозь переднюю
 * стенку, срезая её. Это тот же расчёт с зеркальным знаком.
 *
 * @param {'сзади'|'спереди'} сторона какую пластину снимать
 */
export function плоскостьСоСтороны(bounds, доля = 0.12, ось = 1, сторона = 'сзади') {
  if (сторона !== 'спереди') return плоскостьОтсечения(bounds, доля, ось);
  if (!Array.isArray(bounds) || bounds.length < 6) return null;
  const д = Math.min(0.9, Math.max(0, Number(доля) || 0));
  const мин = Number(bounds[ось * 2]);
  const макс = Number(bounds[ось * 2 + 1]);
  if (!Number.isFinite(мин) || !Number.isFinite(макс) || макс <= мин) return null;
  const уровень = мин + (макс - мин) * д;          // от ПЕРЕДНЕЙ границы
  const origin = [
    (Number(bounds[0]) + Number(bounds[1])) / 2,
    (Number(bounds[2]) + Number(bounds[3])) / 2,
    (Number(bounds[4]) + Number(bounds[5])) / 2,
  ];
  origin[ось] = уровень;
  const normal = [0, 0, 0];
  normal[ось] = 1;   // видно то, что СЗАДИ плоскости: передняя пластина снята
  return { origin, normal, уровень };
}

/**
 * [11.1.46] ОКНО ОБЗОРА — коробка из шести плоскостей.
 *
 * ЧЕСТНО О ТОМ, ЧТО ЭТО: плоскости отсечения работают ПЕРЕСЕЧЕНИЕМ
 * полупространств, поэтому «дырку в передней стенке, а остальное тело
 * целое» ими не выразить — для этого нужна булева разность, которой у
 * маппера нет. Коробка даёт обратное и не менее полезное: видно ТОЛЬКО
 * то, что внутри окна, остальное скрыто. Так устроен crop box в станциях.
 *
 * @param {number[]} bounds границы объёма
 * @param {{глубина?:number, размер?:number, сдвигX?:number, сдвигZ?:number, ось?:0|1|2}} о
 *   глубина — какую долю тела вглубь от передней поверхности показывать;
 *   размер — доля ширины/высоты окна (1 = во всю грудь);
 *   сдвигX/сдвигZ — смещение окна от центра, −1…1.
 * @returns {Array<{origin:number[], normal:number[]}>|null}
 */
export function плоскостиОкнаОбзора(bounds, о = {}) {
  if (!Array.isArray(bounds) || bounds.length < 6) return null;
  const ось = [0, 1, 2].includes(о.ось) ? о.ось : 1;
  const зажать = (v, н, в, у) => (Number.isFinite(Number(v)) ? Math.min(в, Math.max(н, Number(v))) : у);
  const глубина = зажать(о.глубина, 0.05, 1, 0.5);
  const размер = зажать(о.размер, 0.1, 1, 0.5);
  const сдвигX = зажать(о.сдвигX, -1, 1, 0);
  const сдвигZ = зажать(о.сдвигZ, -1, 1, 0);
  const мин = (i) => Number(bounds[i * 2]);
  const макс = (i) => Number(bounds[i * 2 + 1]);
  for (let i = 0; i < 3; i++) if (!Number.isFinite(мин(i)) || !Number.isFinite(макс(i)) || макс(i) <= мин(i)) return null;

  const центр = [0, 1, 2].map((i) => (мин(i) + макс(i)) / 2);
  const плоскости = [];
  const добавить = (i, уровень, знак) => {
    const origin = центр.slice();
    origin[i] = уровень;
    const normal = [0, 0, 0];
    normal[i] = знак;
    плоскости.push({ origin, normal });
  };

  // Ось «перед-зад»: от передней поверхности вглубь на долю «глубина».
  const толщина = (макс(ось) - мин(ось)) * глубина;
  добавить(ось, мин(ось), 1);                 // всё впереди тела не нужно
  добавить(ось, мин(ось) + толщина, -1);      // и глубже окна тоже

  // Две поперечные оси: окно доли «размер» вокруг центра со сдвигом.
  const поперёк = [0, 1, 2].filter((i) => i !== ось);
  const сдвиги = { [поперёк[0]]: сдвигX, [поперёк[1]]: сдвигZ };
  for (const i of поперёк) {
    const полу = (макс(i) - мин(i)) * размер / 2;
    const ц = центр[i] + (макс(i) - мин(i)) / 2 * (сдвиги[i] || 0) * (1 - размер);
    добавить(i, ц - полу, 1);
    добавить(i, ц + полу, -1);
  }
  return плоскости;
}

export default { плоскостьОтсечения, плоскостьСоСтороны, плоскостиОкнаОбзора, маперПоддерживает };
